import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'

// Cria um axios local com a baseURL do .env para evitar dependência circular com api.js
const apiBaseURL = process.env.VUE_APP_API_URL || 'https://localhost:7075/api'
const authAxios = axios.create({ baseURL: apiBaseURL, headers: { 'Content-Type': 'application/json' } })

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem('authToken') || null)
  const refreshToken = ref(localStorage.getItem('refreshToken') || null)
  const utilizador = ref(null)
  const permissoes = ref([]) // Armazena as chaves de permissão

  // GUARDIÃO: Novo estado para armazenar o Nível Fiscal extraído do Token
  const lstNivelModuloFiscal = ref(NV_MODULO_FISCAL.Nenhum)

  // ==========================================
  // REFRESH TOKEN: Gerenciamento de fila para evitar refresh simultâneo
  // ==========================================
  const isRefreshing = ref(false)
  const failedQueue = []

  function processQueue (error, token = null) {
    failedQueue.forEach(prom => {
      if (error) prom.reject(error)
      else prom.resolve(token)
    })
    failedQueue.length = 0
  }

  const isAuthenticated = computed(() => !!token.value)
  const isAdminInterno = computed(() => utilizador.value?.adminInterno === true)
  const isAdminCliente = computed(() => utilizador.value?.adminCliente === true)
  const nomeUtilizador = computed(() => utilizador.value?.nome || 'Utilizador')
  const idEmpresaUtilizador = computed(() => utilizador.value?.idEmpresa || null)

  // GUARDIÃO: Estado para armazenar a empresa selecionada pelo AdminInterno no MainDashboard
  const idEmpresaSelecionada = ref(localStorage.getItem('idEmpresaSelecionada') ? parseInt(localStorage.getItem('idEmpresaSelecionada'), 10) : null)

  // GUARDIÃO: Propriedade Mestra de Contexto (O coração do Multi-Tenant Frontend)
  const empresaAtiva = computed(() => {
    if (isAdminInterno.value) {
      return idEmpresaSelecionada.value
    }
    return idEmpresaUtilizador.value
  })

  function setEmpresaSelecionada (id) {
    idEmpresaSelecionada.value = id
    if (id) {
      localStorage.setItem('idEmpresaSelecionada', id)
    } else {
      localStorage.removeItem('idEmpresaSelecionada')
    }
  }

  // ==========================================
  // MULTI-ORGANIZAÇÃO: Organização ativa (separada de empresaAtiva)
  // ==========================================
  // A empresa roteia o banco (tenant); a organização é o escopo de dado dentro do banco.
  const idOrganizacaoSelecionada = ref(localStorage.getItem('idOrganizacaoSelecionada') ? parseInt(localStorage.getItem('idOrganizacaoSelecionada'), 10) : null)
  // Organização inicial do usuário (claim emitida no login)
  const organizacaoInicial = ref(null)

  const organizacaoAtiva = computed(() => {
    return idOrganizacaoSelecionada.value || organizacaoInicial.value
  })

  function setOrganizacaoSelecionada (id) {
    idOrganizacaoSelecionada.value = id
    if (id) {
      localStorage.setItem('idOrganizacaoSelecionada', id)
    } else {
      localStorage.removeItem('idOrganizacaoSelecionada')
    }
  }

  function limparSessaoLocal () {
    if (token.value || localStorage.getItem('authToken')) {
      localStorage.removeItem('authToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('idEmpresaSelecionada')
      localStorage.removeItem('idOrganizacaoSelecionada')
      token.value = null
      refreshToken.value = null
      utilizador.value = null
      permissoes.value = []
      idEmpresaSelecionada.value = null
      idOrganizacaoSelecionada.value = null
      organizacaoInicial.value = null
      lstNivelModuloFiscal.value = NV_MODULO_FISCAL.Nenhum // Reset do nível fiscal
      axios.defaults.headers.common.Authorization = ''
      // Também limpa na instância do nosso serviço api, se necessário
    }
  }

  function descodificarToken (tokenString) {
    if (tokenString) {
      try {
        // CORREÇÃO: atob() decodifica base64 como Latin-1, quebrando caracteres UTF-8 multi-byte (ex: "í" vira "Ã­")
        // Usamos decodeURIComponent + escape para converter corretamente os bytes para UTF-8
        const base64Url = tokenString.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        }).join(''))
        const payload = JSON.parse(jsonPayload)

        const nowInSeconds = Math.floor(Date.now() / 1000)
        if (payload.exp && payload.exp < nowInSeconds) {
          limparSessaoLocal()
          return
        }

        utilizador.value = {
          id: payload.sub,
          email: payload.email,
          nome: payload.nome,
          adminInterno: payload.adminInterno === 'true',
          adminCliente: payload.adminCliente === 'true',
          idEmpresa: payload.idEmpresa ? parseInt(payload.idEmpresa, 10) : null
        }

        permissoes.value = payload.permissoes || []

        // GUARDIÃO: Organização inicial do usuário (claim emitida no login)
        organizacaoInicial.value = payload.organizacaoInicial ? parseInt(payload.organizacaoInicial, 10) : null

        // GUARDIÃO: Extração da nova claim do Token!
        lstNivelModuloFiscal.value = payload.lstNivelModuloFiscal || NV_MODULO_FISCAL.Nenhum

        axios.defaults.headers.common.Authorization = `Bearer ${tokenString}`
      } catch (e) {
        console.error('Erro ao descodificar token inválido:', e)
        limparSessaoLocal()
      }
    } else {
      limparSessaoLocal()
    }
  }

  async function login (email, senha) {
    try {
      const response = await authAxios.post('/Auth/login', { email, senha })
      if (response.data && response.data.token && response.data.refreshToken) {
        const newToken = response.data.token
        const newRefreshToken = response.data.refreshToken
        localStorage.setItem('authToken', newToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        token.value = newToken
        refreshToken.value = newRefreshToken
        descodificarToken(newToken)
        return true
      } else {
        return false
      }
    } catch (error) {
      return false
    }
  }

  async function refreshAccessToken () {
    const currentRefreshToken = localStorage.getItem('refreshToken')
    if (!currentRefreshToken) {
      throw new Error('No refresh token available')
    }

    try {
      const response = await authAxios.post('/Auth/refresh-token', {
        refreshToken: currentRefreshToken
      })

      if (response.data && response.data.token && response.data.refreshToken) {
        localStorage.setItem('authToken', response.data.token)
        localStorage.setItem('refreshToken', response.data.refreshToken)
        token.value = response.data.token
        refreshToken.value = response.data.refreshToken
        descodificarToken(response.data.token)
        return response.data.token
      }
      throw new Error('Invalid refresh response')
    } catch (error) {
      limparSessaoLocal()
      throw error
    }
  }

  async function logout () {
    // Captura o token ANTES de limpar — será usado para notificar o backend
    const tokenAtual = localStorage.getItem('authToken')

    // GUARDIÃO: limpa a sessão local de forma SÍNCRONA primeiro.
    // Isso garante que authToken/refreshToken sejam removidos do localStorage
    // mesmo que a página seja descarregada imediatamente por uma navegação
    // (ex: window.location.href = '/' no handleLogout do MainDashboard).
    // Sem isso, a guarda de navegação reautenticava o usuário no reload,
    // fazendo o logout "não surtir efeito" na primeira tentativa.
    limparSessaoLocal()

    // Best-effort: avisa o backend para revogar o refresh token.
    // Não bloqueia o fluxo de logout do frontend, que já limpou a sessão local.
    if (tokenAtual) {
      try {
        await authAxios.post('/Auth/logout', {}, {
          headers: { Authorization: `Bearer ${tokenAtual}` }
        })
      } catch (e) {
        // Silencia erro - se der 401 já está invalidado
      }
    }
  }

  function pode (chave) {
    if (isAdminInterno.value) {
      return true
    }

    const isSistema = [
      'EMPRESAS_',
      'PLANOS_',
      'REGRASACESSO_',
      'AVANCADAS_'
    ].some(prefix => chave.startsWith(prefix))

    if (isSistema) {
      return false
    }

    if (isAdminCliente.value) {
      return true
    }

    return permissoes.value.includes(chave)
  }

  // Verifica se o usuário tem QUALQUER permissão para um módulo (independente da chave)
  // Usado para controlar visibilidade de itens de menu
  // Suporta tanto chaves de módulo (ex: COMPRAS → prefixo COMPRAS_)
  // quanto chaves de permissão específica (ex: COMPRAS_-_OC_APROVAR → casamento exato)
  function temModulo (modulo) {
    if (isAdminInterno.value) return true
    if (isAdminCliente.value) return true
    const chave = modulo.toUpperCase()
    const prefix = chave + '_'
    // Tenta prefixo (ex: COMPRAS_ → acha COMPRAS_-_OC_APROVAR)
    // Depois tenta exato (ex: COMPRAS_-_OC_APROVAR → acha ela mesma)
    return permissoes.value.some(p => p.startsWith(prefix)) || permissoes.value.includes(chave)
  }

  // Retorna todas as strings MODULO_CHAVE do módulo ativo
  // Usado pelas telas filhas para controlar funções internas
  function getPermsDoModulo (modulo) {
    if (isAdminInterno.value || isAdminCliente.value) return ['COMPLETO']
    const prefix = modulo.toUpperCase() + '_'
    return permissoes.value.filter(p => p.startsWith(prefix))
  }

  descodificarToken(token.value)

  // ESCUTADOR DE STORAGE: Sincroniza o logout entre abas em tempo real
  // Se o token for removido em outra aba (ou no F12), esta aba também desloga
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key === 'authToken' && !event.newValue) {
        logout()
        // O redirecionamento será tratado pelo App.vue ou pelo próximo Interceptor para evitar import circular
      }
    })
  }

  return {
    token,
    refreshToken,
    utilizador,
    isAuthenticated,
    isAdminInterno,
    isAdminCliente,
    nomeUtilizador,
    idEmpresaUtilizador,
    idEmpresaSelecionada,
    empresaAtiva,
    setEmpresaSelecionada,
    idOrganizacaoSelecionada,
    organizacaoInicial,
    organizacaoAtiva,
    setOrganizacaoSelecionada,
    permissoes,
    lstNivelModuloFiscal,
    isRefreshing,
    failedQueue,
    processQueue,
    refreshAccessToken,
    pode,
    temModulo,
    getPermsDoModulo,
    login,
    logout,
    limparSessaoLocal
  }
})
